/**
 * Tech Tree Data
 * Defines all innovation nodes with their positions, categories and costs (their
 * names and descriptions are the locales', under innovation.nodes);
 * how they connect, drawn as the tree's lines; and where each stands for a team.
 */

import { COMMERCIAL_TECHNOLOGIES } from '../constants/commercial';

/** The kinds of node on the tree: what researching one changes, and the root. */
export const TECH_CATEGORIES = ['plane', 'fleet', 'research', 'policy', 'deal', 'start'] as const;

export type TechCategory = (typeof TECH_CATEGORIES)[number];

export interface TechNode {
  id: string;
  category: TechCategory;
  row: number;
  col: number;
  cost: number; // Knowledge points required to unlock
  prerequisite?: string; // Innovation ID that must be unlocked first (for plane upgrade chains)
}

// Grid dimensions
export const GRID_COLS = 11;
export const GRID_ROWS = 11;

// Center position (START)
export const START_ROW = 5;
export const START_COL = 5;

// Cost tiers based on distance from center
export const COST_TIER_1 = 20;  // Adjacent to START (distance 1)
export const COST_TIER_2 = 50;  // Distance 2 from START
export const COST_TIER_3 = 100;  // Distance 3+ from START

// All tech nodes from the diagram (offset +2 from original 7x7 to center in 11x11)
export const TECH_NODES: TechNode[] = [
  {
    id: COMMERCIAL_TECHNOLOGIES.advisor,
    category: 'research', row: 5, col: 1, cost: COST_TIER_1,
  },
  {
    id: COMMERCIAL_TECHNOLOGIES.wonder,
    category: 'research', row: 5, col: 0, cost: COST_TIER_2,
    prerequisite: COMMERCIAL_TECHNOLOGIES.advisor,
  },
  // Row 2 (was row 0, distance 3 from center)
  {
    id: 'hawk_xlr_v3',
    category: 'plane',
    row: 2,
    col: 3,
    cost: COST_TIER_3,
    prerequisite: 'hawk_xl_v2',
  },
  {
    id: 'hawk_xl_v2',
    category: 'plane',
    row: 2,
    col: 4,
    cost: COST_TIER_3,
    prerequisite: 'hawk_v1',
  },
  {
    id: 'condor_max_v2',
    category: 'plane',
    row: 2,
    col: 6,
    cost: COST_TIER_3,
    prerequisite: 'condor_v1',
  },
  {
    id: 'condor_ultra_v3',
    category: 'plane',
    row: 2,
    col: 7,
    cost: COST_TIER_3,
    prerequisite: 'condor_max_v2',
  },
  {
    id: 'osprey_lr_v2',
    category: 'plane',
    row: 2,
    col: 8,
    cost: COST_TIER_3,
    prerequisite: 'osprey_v1',
  },

  // Row 3 (was row 1, distance 2 from center)
  {
    id: 'massage_seats',
    category: 'fleet',
    row: 3,
    col: 3,
    cost: COST_TIER_2,
  },
  {
    id: 'hawk_v1',
    category: 'plane',
    row: 3,
    col: 4,
    cost: COST_TIER_2,
  },
  {
    id: 'condor_v1',
    category: 'plane',
    row: 3,
    col: 5,
    cost: COST_TIER_2,
  },
  {
    id: 'osprey_v1',
    category: 'plane',
    row: 3,
    col: 6,
    cost: COST_TIER_2,
  },
  {
    id: 'ultimate_luxury',
    category: 'policy',
    row: 3,
    col: 7,
    cost: COST_TIER_2,
  },
  {
    id: 'eagle_freedom_v3',
    category: 'plane',
    row: 3,
    col: 8,
    cost: COST_TIER_3,
    prerequisite: 'eagle_liberty_v2',
  },

  // Row 4 (was row 2, mixed distances)
  {
    id: 'cargo_logistics',
    category: 'policy',
    row: 4,
    col: 4,
    cost: COST_TIER_2,
  },
  {
    id: 'manufacturer_relations',
    category: 'deal',
    row: 4,
    col: 6,
    cost: COST_TIER_1,
  },
  {
    id: 'eagle_v1',
    category: 'plane',
    row: 4,
    col: 7,
    cost: COST_TIER_2,
  },
  {
    id: 'eagle_liberty_v2',
    category: 'plane',
    row: 4,
    col: 8,
    cost: COST_TIER_3,
    prerequisite: 'eagle_v1',
  },

  // Row 5 (was row 3, middle row with START)
  {
    id: 'falcon_ultimate_v2',
    category: 'plane',
    row: 5,
    col: 2,
    cost: COST_TIER_3,
    prerequisite: 'falcon_v1',
  },
  {
    id: 'falcon_v1',
    category: 'plane',
    row: 5,
    col: 3,
    cost: COST_TIER_2,
  },
  {
    id: 'start',
    category: 'start',
    row: START_ROW,
    col: START_COL,
    cost: 0,
  },
  {
    id: 'pelican_v1',
    category: 'plane',
    row: 5,
    col: 7,
    cost: COST_TIER_2,
  },
  {
    id: 'higher_pay',
    category: 'policy',
    row: 5,
    col: 8,
    cost: COST_TIER_3,
  },

  // Row 6 (was row 4, mixed distances)
  {
    id: 'super_fuel',
    category: 'fleet',
    row: 6,
    col: 2,
    cost: COST_TIER_3,
  },
  {
    id: 'dynamic_pricing',
    category: 'policy',
    row: 6,
    col: 3,
    cost: COST_TIER_2,
  },
  {
    id: 'sparrow_mark2_v2',
    category: 'plane',
    row: 6,
    col: 4,
    cost: COST_TIER_1,
  },
  {
    id: 'priority_landing',
    category: 'deal',
    row: 6,
    col: 7,
    cost: COST_TIER_2,
  },
  {
    id: 'pelican_super_v2',
    category: 'plane',
    row: 6,
    col: 8,
    cost: COST_TIER_3,
    prerequisite: 'pelican_v1',
  },

  // Row 7 (was row 5, distance 2 from center)
  {
    id: 'sparrow_ev_v3',
    category: 'plane',
    row: 7,
    col: 3,
    cost: COST_TIER_2,
    prerequisite: 'sparrow_mark2_v2',
  },
  {
    id: 'swift_plus_v2',
    category: 'plane',
    row: 7,
    col: 4,
    cost: COST_TIER_2,
  },
  {
    id: 'standing_room',
    category: 'fleet',
    row: 7,
    col: 5,
    cost: COST_TIER_2,
  },
  {
    id: 'no_food_service',
    category: 'policy',
    row: 7,
    col: 6,
    cost: COST_TIER_2,
  },
  {
    id: 'self_service',
    category: 'policy',
    row: 7,
    col: 7,
    cost: COST_TIER_2,
  },

  // Row 8 (was row 6, distance 3 from center)
  {
    id: 'robotic_ground_crews',
    category: 'policy',
    row: 8,
    col: 4,
    cost: COST_TIER_3,
  },
  {
    id: 'swift_pro_v3',
    category: 'plane',
    row: 8,
    col: 5,
    cost: COST_TIER_3,
    prerequisite: 'swift_plus_v2',
  },
  {
    id: 'aggressive_overbooking',
    category: 'policy',
    row: 8,
    col: 6,
    cost: COST_TIER_3,
  },
];

// Helper: create a lookup map by grid position
export function createGridMap(): Map<string, TechNode> {
  const map = new Map<string, TechNode>();
  for (const node of TECH_NODES) {
    map.set(`${node.row},${node.col}`, node);
  }
  return map;
}

// Helper: get all adjacent positions (4 directions, no diagonals)
export function getAdjacentPositions(row: number, col: number): Array<{ row: number; col: number }> {
  const candidates = [
    { row: row - 1, col }, // Up
    { row: row + 1, col }, // Down
    { row, col: col - 1 }, // Left
    { row, col: col + 1 }, // Right
  ];

  return candidates.filter(
    (pos) => pos.row >= 0 && pos.row < GRID_ROWS && pos.col >= 0 && pos.col < GRID_COLS
  );
}

// Original building cells bridge their surviving neighbors. These are layout
// coordinates, not unlockable nodes. Must match the server's InnovationConnections.
const RETIRED_BRIDGE_POSITIONS = new Set(['2,5', '4,3', '4,5', '5,4', '5,6', '6,5', '6,6']);

export const TECH_CONNECTIONS: Record<string, string[]> = (() => {
  const grid = new Map(TECH_NODES.filter(node => node.category !== 'research')
    .map(node => [`${node.row},${node.col}`, node]));
  const connections: Record<string, string[]> = {};
  for (const node of grid.values()) {
    const visited = new Set([`${node.row},${node.col}`]);
    const queue = [{ row: node.row, col: node.col }];
    const neighbors = new Set<string>();
    while (queue.length) {
      const current = queue.shift()!;
      for (const next of getAdjacentPositions(current.row, current.col)) {
        const key = `${next.row},${next.col}`;
        if (visited.has(key)) continue;
        visited.add(key);
        const neighbor = grid.get(key);
        if (neighbor) neighbors.add(neighbor.id);
        else if (RETIRED_BRIDGE_POSITIONS.has(key)) queue.push(next);
      }
    }
    connections[node.id] = [...neighbors].sort();
  }
  connections.start = [...connections.start, COMMERCIAL_TECHNOLOGIES.advisor].sort();
  connections[COMMERCIAL_TECHNOLOGIES.advisor] = [COMMERCIAL_TECHNOLOGIES.wonder, 'start'];
  connections[COMMERCIAL_TECHNOLOGIES.wonder] = [COMMERCIAL_TECHNOLOGIES.advisor];
  return connections;
})();

/**
 * The innovation a node waits for before it can be researched — the model
 * before it in an aircraft's chain, the Research Methods before the Wonder's
 * blueprint — or undefined when it waits for none. The server holds the same
 * chains (InnovationPrerequisite, innovation.go).
 */
export function prerequisiteOf(nodeId: string | null | undefined): string | undefined {
  return TECH_NODES.find((node) => node.id === nodeId)?.prerequisite;
}

export function canUnlockInnovation(nodeId: string, unlocked: ReadonlySet<string>): boolean {
  const node = TECH_NODES.find(candidate => candidate.id === nodeId);
  if (!node || nodeId === 'start' || unlocked.has(nodeId)) return false;
  const prerequisite = node.prerequisite;
  return (!prerequisite || unlocked.has(prerequisite)) &&
    (TECH_CONNECTIONS[nodeId] ?? []).some(id => id === 'start' || unlocked.has(id));
}

/** Every innovation there is to research: the tree's root is where it starts, not one of them. */
export const INNOVATION_COUNT = TECH_NODES.filter((node) => node.category !== 'start').length;

/**
 * What a team has researched, from its record (`team.unlocked_innovations`):
 * the nodes on the tree it names, never START, which every team holds.
 */
export function researchedInnovations(unlocked: Readonly<Record<string, boolean>> | null | undefined): ReadonlySet<string> {
  return new Set(TECH_NODES.filter((node) => node.category !== 'start' && unlocked?.[node.id]).map((node) => node.id));
}

/**
 * Where a node stands for a team, as the server would judge a request for it:
 *
 *   start        the root, the team's from the start
 *   researched   the team's
 *   open         it can be researched now: connected to something researched,
 *                its prerequisite researched (canUnlockInnovation)
 *   blocked      beside the team's path, but behind a prerequisite
 *   far          nothing it is connected to is researched
 */
export type NodeStanding = 'start' | 'researched' | 'open' | 'blocked' | 'far';

export function nodeStanding(nodeId: string, researched: ReadonlySet<string>): NodeStanding {
  if (nodeId === 'start') return 'start';
  if (researched.has(nodeId)) return 'researched';
  if (canUnlockInnovation(nodeId, researched)) return 'open';
  const besidePath = (TECH_CONNECTIONS[nodeId] ?? []).some((id) => id === 'start' || researched.has(id));
  return besidePath ? 'blocked' : 'far';
}

/** The team's own: START and whatever it has researched. */
function isTeams(standing: NodeStanding): boolean {
  return standing === 'start' || standing === 'researched';
}

// ==================== THE LINES ====================

/** A cell of the tree's grid, by row and column. */
export interface GridCell {
  row: number;
  col: number;
}

/**
 * A stretch of the tree's lines between two neighbouring cells, and the
 * connections it draws: each pair of nodes TECH_CONNECTIONS joins through it.
 * Connections run through the retired cells, so the stretch from a node into
 * one carries every connection the node makes through it.
 */
export interface TechLine {
  from: GridCell;
  to: GridCell;
  joins: ReadonlyArray<readonly [string, string]>;
}

/** A retired cell where three or more stretches meet: a dot on the tree. */
interface TechJunction extends GridCell {
  /** The stretches that meet there, as indexes into TECH_LINES. */
  lines: readonly number[];
}

const cellKey = (cell: GridCell) => `${cell.row},${cell.col}`;

/**
 * How far each cell is from `from` through retired cells alone, as
 * TECH_CONNECTIONS walks them: a retired cell passes the walk on, a node ends
 * it. The research branch is off the walk, as it is off TECH_CONNECTIONS'.
 */
function distancesThroughRetired(from: GridCell, nodes: ReadonlyMap<string, TechNode>): Map<string, number> {
  const distance = new Map([[cellKey(from), 0]]);
  const queue: GridCell[] = [from];
  while (queue.length) {
    const current = queue.shift()!;
    const next = distance.get(cellKey(current))! + 1;
    for (const neighbour of getAdjacentPositions(current.row, current.col)) {
      const key = cellKey(neighbour);
      if (distance.has(key)) continue;
      if (nodes.has(key)) distance.set(key, next);
      else if (RETIRED_BRIDGE_POSITIONS.has(key)) {
        distance.set(key, next);
        queue.push(neighbour);
      }
    }
  }
  return distance;
}

/**
 * The tree's lines: every connection TECH_CONNECTIONS makes, drawn along the
 * grid — straight between neighbours, through the retired cells between
 * those it joins across them, along every shortest way through. The one
 * connection the grid cannot draw is the research branch's to START, which
 * joins it by an edge of its own rather than its place: the tree labels that
 * corner instead (From START).
 */
export const TECH_LINES: readonly TechLine[] = (() => {
  const byPosition = createGridMap();
  const walked = new Map([...byPosition].filter(([, node]) => node.category !== 'research'));
  const lines = new Map<string, { from: GridCell; to: GridCell; joins: Array<readonly [string, string]> }>();
  const draw = (a: GridCell, b: GridCell, join: readonly [string, string]) => {
    const [from, to] = cellKey(a) < cellKey(b) ? [a, b] : [b, a];
    const key = `${cellKey(from)}|${cellKey(to)}`;
    const line = lines.get(key) ?? { from, to, joins: [] };
    line.joins.push(join);
    lines.set(key, line);
  };

  for (const node of TECH_NODES) {
    for (const otherId of TECH_CONNECTIONS[node.id] ?? []) {
      if (otherId <= node.id) continue;
      const other = TECH_NODES.find((candidate) => candidate.id === otherId)!;
      const join = [node.id, other.id] as const;
      if (!walked.has(cellKey(node)) || !walked.has(cellKey(other))) {
        // The research branch: drawn only where its nodes are neighbours.
        if (Math.abs(node.row - other.row) + Math.abs(node.col - other.col) === 1) draw(node, other, join);
        continue;
      }
      const fromNode = distancesThroughRetired(node, walked);
      const fromOther = distancesThroughRetired(other, walked);
      const length = fromNode.get(cellKey(other))!;
      // A step is on a shortest way when the way to it, the step and the way on add up.
      for (const [key, near] of fromNode) {
        if (key !== cellKey(node) && !RETIRED_BRIDGE_POSITIONS.has(key)) continue;
        const [row, col] = key.split(',').map(Number);
        for (const next of getAdjacentPositions(row, col)) {
          // The way runs through retired cells: another node on it would end the walk there.
          if (cellKey(next) !== cellKey(other) && !RETIRED_BRIDGE_POSITIONS.has(cellKey(next))) continue;
          const far = fromOther.get(cellKey(next));
          if (far !== undefined && near + 1 + far === length) draw({ row, col }, next, join);
        }
      }
    }
  }
  return [...lines.values()];
})();

/** The retired cells where three or more of the lines meet. */
export const TECH_JUNCTIONS: readonly TechJunction[] = [...RETIRED_BRIDGE_POSITIONS].flatMap((key) => {
  const [row, col] = key.split(',').map(Number);
  const meeting = TECH_LINES.flatMap((line, index) => (cellKey(line.from) === key || cellKey(line.to) === key ? [index] : []));
  return meeting.length >= 3 ? [{ row, col, lines: meeting }] : [];
});

/**
 * How a stretch of line is drawn for a team, by the strongest connection it
 * carries: `path` between two of the team's nodes — the way it came; `reach`
 * from one of them to a node it can research now; `faint` otherwise.
 */
export type LineStanding = 'path' | 'reach' | 'faint';

export function lineStanding(line: TechLine, standingOf: (nodeId: string) => NodeStanding): LineStanding {
  let strongest: LineStanding = 'faint';
  for (const [a, b] of line.joins) {
    const first = standingOf(a);
    const second = standingOf(b);
    if (isTeams(first) && isTeams(second)) return 'path';
    if ((isTeams(first) && second === 'open') || (isTeams(second) && first === 'open')) strongest = 'reach';
  }
  return strongest;
}
